import { evaluate } from 'mathjs';
import seedrandom from 'seedrandom';

import type {
  RawContentSection,
  RawContentUnit,
  RawQuestion,
  RawVariable,
  RawVariableContentPair,
} from '@/features/content/useComponent';

export interface ResolvedRandomAnswer {
  exact?: string;
  extraExponent?: number;
  numerical?: number;
}

export interface ResolvedRandomVariables {
  answerIndex: number;
  correctAnswer: ResolvedRandomAnswer;
  generatedOptions: RawContentSection[] | null;
  variables: RawVariable[];
}

interface NumericalComputationResult {
  correctAnswer: ResolvedRandomAnswer;
  evaluatedAnswer: number;
  variables: RawVariable[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toContentUnitArray(value: unknown): RawContentUnit[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawContentUnit[];
}

function toVariableArray(value: unknown): RawVariable[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawVariable[];
}

function toContentPairArray(value: unknown): RawVariableContentPair[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawVariableContentPair[];
}

function getQuestionType(value: unknown): 'exact' | 'multipleChoice' | 'multiPart' | 'numerical' | 'unsupported' {
  if (value === null || value === undefined || value === 0 || value === '0') {
    return 'multipleChoice';
  }

  const normalized = typeof value === 'string' ? value.trim().toUpperCase() : value;

  if (normalized === 1 || normalized === '1' || normalized === 'MULTIPLE_CHOICE') {
    return 'multipleChoice';
  }

  if (normalized === 3 || normalized === '3' || normalized === 'NUMERICAL_WITH_ERROR') {
    return 'numerical';
  }

  if (normalized === 4 || normalized === '4' || normalized === 'EXACT_ANSWER') {
    return 'exact';
  }

  if (normalized === 5 || normalized === '5' || normalized === 'MULTI_PART') {
    return 'multiPart';
  }

  return 'unsupported';
}

function getVariableName(variable: RawVariable) {
  return normalizeString(variable.name) ?? '';
}

function sortVariablesByName(variables: RawVariable[]) {
  return [...variables].sort((left, right) => {
    const leftName = getVariableName(left);
    const rightName = getVariableName(right);

    if (leftName.length !== rightName.length) {
      return rightName.length - leftName.length;
    }

    return leftName.localeCompare(rightName);
  });
}

function roundToSignificantFigures(value: number, precision = 3) {
  if (!Number.isFinite(value) || value === 0) {
    return value;
  }

  return Number.parseFloat(value.toPrecision(precision));
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function extractTextContent(contentSection: RawContentSection | null | undefined): string {
  if (!contentSection) {
    return '';
  }

  return toContentUnitArray(contentSection.content_units ?? contentSection.contentUnits)
    .map((unit) => normalizeString(unit.text))
    .filter((text): text is string => Boolean(text))
    .join(' ')
    .trim();
}

export function getQuestionContentText(question: RawQuestion): string {
  const contentSection = isRecord(question.content_section ?? question.contentSection)
    ? ((question.content_section ?? question.contentSection) as RawContentSection)
    : null;
  const firstTextUnit = toContentUnitArray(
    contentSection?.content_units ?? contentSection?.contentUnits,
  ).find((unit) => normalizeString(unit.text));

  return (
    normalizeString(firstTextUnit?.text) ??
    normalizeString(question.content) ??
    ''
  );
}

export function getSeedForUser(
  question: RawQuestion,
  kuduUserId: string,
  isMultiPart: boolean,
): string {
  const initialSeed =
    normalizeString(
      question.seed_for_random_variables ?? question.seedForRandomVariables,
    ) ?? getQuestionContentText(question);
  let seed = `${initialSeed}${kuduUserId}`;

  if (isMultiPart) {
    seed = kuduUserId;
  }

  return seed;
}

function getVariableReplacementValue(variable: RawVariable): string | null {
  const currentValue = normalizeNumber(variable.current_value ?? variable.currentValue);

  if (currentValue !== undefined) {
    return String(currentValue);
  }

  const contentPairs = toContentPairArray(
    variable.content_pairs ?? variable.contentPairs,
  );
  const currentContentPairIndex = Math.trunc(
    normalizeNumber(
      variable.current_content_pair_index ?? variable.currentContentPairIndex,
    ) ?? -1,
  );

  if (currentContentPairIndex < 0 || currentContentPairIndex >= contentPairs.length) {
    return null;
  }

  const pair = contentPairs[currentContentPairIndex];
  return extractTextContent(
    isRecord(pair.variable_content ?? pair.variableContent)
      ? ((pair.variable_content ?? pair.variableContent) as RawContentSection)
      : null,
  ) || null;
}

export function substituteVariablesInContent(
  text: string,
  variables: RawVariable[],
): string {
  return variables.reduce((result, variable) => {
    const name = getVariableName(variable);
    const replacement = getVariableReplacementValue(variable);

    if (!name || replacement === null) {
      return result;
    }

    return result.replaceAll(`##${name}##`, replacement);
  }, text);
}

// Replace every occurrence of `name` with its value, making IMPLICIT
// MULTIPLICATION explicit. Instructors write "2.5a" for 2.5×a, but a plain text
// substitution turns that into "2.50.305" — a malformed number with two decimal
// points. The evaluator then throws on every retry, so the question produced NO
// correct answer at all: no solution shown, nothing stored, and no student could
// earn credit. Emitting the "*" the author omitted keeps the value the evaluator
// sees identical to the value the author meant.
//
// Only a value directly following a digit or a decimal point is affected — every
// other position (after an operator, "(", or the start) is substituted exactly as
// before, so expressions that already evaluate are byte-for-byte unchanged.
function replaceVariableOccurrences(
  expression: string,
  name: string,
  replacement: string,
): string {
  let result = '';
  let index = 0;

  for (;;) {
    const found = expression.indexOf(name, index);

    if (found === -1) {
      return result + expression.slice(index);
    }

    result += expression.slice(index, found);

    // Inspect what the evaluator would actually read just before this value.
    const previous = result.slice(-1);

    if (previous !== '' && previous >= '0' && previous <= '9') {
      result += '*';
    } else if (previous === '.') {
      result += '*';
    }

    result += replacement;
    index = found + name.length;
  }
}

export function substituteVariablesInExpression(
  expression: string,
  variables: RawVariable[],
): string {
  return sortVariablesByName(variables).reduce((result, variable) => {
    const name = getVariableName(variable);
    const replacement = getVariableReplacementValue(variable);

    if (!name || replacement === null) {
      return result;
    }

    return replaceVariableOccurrences(result, name, replacement);
  }, expression);
}

/** Common syntax from other tools that mathjs does not accept. */
const EXPRESSION_HINTS: { hint: string; pattern: RegExp }[] = [
  { hint: 'use acos, asin or atan rather than arccos, arcsin or arctan', pattern: /\barc(cos|sin|tan)\s*\(/i },
  { hint: 'pi is a constant, so write pi rather than pi()', pattern: /\bpi\s*\(\s*\)/i },
  { hint: 'use log(x) for natural log and log10(x) for base 10', pattern: /\bln\s*\(/i },
];

export interface VariableAnswerEvaluation {
  /** Why it could not be evaluated, phrased for the instructor. */
  error?: string;
  /**
   * Rounded exactly as the graded answer is. Students are marked against the
   * three-significant-figure value, so showing the raw double would advertise a
   * precision the grading does not have.
   */
  value?: number;
}

/**
 * Evaluate a variable-based answer against concrete variable values, reporting
 * WHY it failed instead of swallowing the error.
 *
 * computeRandomNumericalAnswer deliberately retries and discards its errors, so
 * an expression mathjs cannot parse — "arccos(Fx/F)*(180/pi())", where the
 * function is spelled acos and pi is a constant — simply yields no answer. The
 * question then renders perfectly, with real numbers in the prompt, and the
 * missing answer only shows up as every student being marked wrong. This is the
 * path that surfaces the reason: the editor blocks the save, and the preview
 * card shows it.
 */
export function evaluateVariableBasedAnswer(
  expression: string | null | undefined,
  variables: RawVariable[],
): VariableAnswerEvaluation {
  const trimmed = normalizeString(expression);

  if (!trimmed) {
    return { error: 'No answer formula is set.' };
  }

  const substituted = substituteVariablesInExpression(trimmed, variables);

  try {
    const value = Number(evaluate(substituted));

    if (!Number.isFinite(value)) {
      return {
        error: `The formula did not produce a number for ${describeVariableValues(variables)}.`,
      };
    }

    return { value: roundToSignificantFigures(value) };
  } catch (error) {
    const detail =
      error instanceof Error ? error.message.split('\n')[0].trim() : String(error);
    const hint = EXPRESSION_HINTS.find(({ pattern }) => pattern.test(trimmed))?.hint;

    return { error: hint ? `${detail} — ${hint}.` : detail };
  }
}

/** "F = 0.408, Fx = 0.19", for an error message that can be acted on. */
export function describeVariableValues(variables: RawVariable[]) {
  return sortVariablesByName(variables)
    .map((variable) => {
      const name = getVariableName(variable);
      const value = getVariableReplacementValue(variable);

      return name && value !== null ? `${name} = ${value}` : null;
    })
    .filter((entry): entry is string => entry !== null)
    .join(', ');
}

function cloneContentSection(contentSection: RawContentSection | null | undefined): RawContentSection | null {
  if (!contentSection) {
    return null;
  }

  return {
    ...contentSection,
    content_units: toContentUnitArray(
      contentSection.content_units ?? contentSection.contentUnits,
    ).map((unit) => ({ ...unit })),
    contentUnits: toContentUnitArray(
      contentSection.content_units ?? contentSection.contentUnits,
    ).map((unit) => ({ ...unit })),
  };
}

function createTextContentSection(text: string): RawContentSection {
  const html = `<p>${escapeHtml(text)}</p>`;

  return {
    content_units: [
      {
        text: html,
        type: 'TEXT',
      },
    ],
  };
}

function getContentPairs(variable: RawVariable): RawVariableContentPair[] {
  return toContentPairArray(variable.content_pairs ?? variable.contentPairs);
}

function isRandomSetQuestion(variables: RawVariable[]) {
  return variables.length > 0 && getContentPairs(variables[0]).length > 0;
}

function normalizeCorrectNumericalAnswer(value: number): ResolvedRandomAnswer {
  const roundedValue = roundToSignificantFigures(value);
  const absoluteValue = Math.abs(roundedValue);

  if (absoluteValue > 1e9 && absoluteValue < 1e22) {
    return {
      extraExponent: 12,
      numerical: roundToSignificantFigures(roundedValue / 1e12),
    };
  }

  return {
    numerical: roundedValue,
  };
}

function formatResolvedAnswer(answer: ResolvedRandomAnswer): string {
  if (answer.exact) {
    return answer.exact;
  }

  if (answer.numerical === undefined) {
    return '';
  }

  return answer.extraExponent !== undefined
    ? `${answer.numerical}e${answer.extraExponent}`
    : String(answer.numerical);
}

function resolvePairCorrectAnswer(pair: RawVariableContentPair): ResolvedRandomAnswer {
  const numerical =
    normalizeNumber(pair.answer_numerical ?? pair.answerNumerical) ??
    normalizeNumber(pair.answer);

  if (numerical !== undefined) {
    return normalizeCorrectNumericalAnswer(numerical);
  }

  return {
    exact:
      normalizeString(pair.answer_string ?? pair.answerString) ??
      normalizeString(pair.answer) ??
      undefined,
  };
}

function getPairAnswerContent(pair: RawVariableContentPair): RawContentSection | null {
  const answerContent = isRecord(pair.answer_content ?? pair.answerContent)
    ? ((pair.answer_content ?? pair.answerContent) as RawContentSection)
    : null;

  if (answerContent) {
    return cloneContentSection(answerContent);
  }

  const exactAnswer =
    normalizeString(pair.answer_string ?? pair.answerString) ??
    normalizeString(pair.answer);

  if (exactAnswer) {
    return createTextContentSection(exactAnswer);
  }

  const numericalAnswer =
    normalizeNumber(pair.answer_numerical ?? pair.answerNumerical) ??
    normalizeNumber(pair.answer);

  if (numericalAnswer !== undefined) {
    return createTextContentSection(
      formatResolvedAnswer(normalizeCorrectNumericalAnswer(numericalAnswer)),
    );
  }

  return null;
}

function getMultipleChoiceOptionCount(question: RawQuestion) {
  return Array.isArray(question.options) && question.options.length > 0
    ? question.options.length
    : 5;
}

function differsByAtLeastTenPercent(candidate: number, existingValues: number[]) {
  return existingValues.every((existingValue) => {
    const baseline = Math.abs(existingValue);

    if (baseline < 1e-9) {
      return Math.abs(candidate - existingValue) > 0.1;
    }

    return Math.abs(candidate - existingValue) / baseline > 0.1;
  });
}

function cloneVariables(variables: RawVariable[]) {
  return variables.map((variable) => ({
    ...variable,
    content_pairs: getContentPairs(variable).map((pair) => ({ ...pair })),
    contentPairs: getContentPairs(variable).map((pair) => ({ ...pair })),
  }));
}

function assignNumericalVariableValues(
  variables: RawVariable[],
  rng: seedrandom.PRNG,
): RawVariable[] {
  const clonedVariables = cloneVariables(variables).map((variable, index) => ({
    originalIndex: index,
    variable,
  }));
  const sortedVariables = [...clonedVariables].sort((left, right) => {
    const leftName = getVariableName(left.variable);
    const rightName = getVariableName(right.variable);

    if (leftName.length !== rightName.length) {
      return rightName.length - leftName.length;
    }

    return leftName.localeCompare(rightName);
  });

  sortedVariables.forEach(({ variable }) => {
    const min = normalizeNumber(variable.min_value ?? variable.minValue) ?? 0;
    const max = normalizeNumber(variable.max_value ?? variable.maxValue) ?? min;
    const lowerBound = Math.min(min, max);
    const upperBound = Math.max(min, max);
    const generatedValue =
      upperBound === lowerBound
        ? lowerBound
        : (upperBound - lowerBound) * rng.quick() + lowerBound;
    const roundedValue = roundToSignificantFigures(generatedValue);

    variable.current_value = roundedValue;
    variable.currentValue = roundedValue;
  });

  return clonedVariables
    .sort((left, right) => left.originalIndex - right.originalIndex)
    .map(({ variable }) => variable);
}

function computeRandomNumericalAnswer(
  question: RawQuestion,
  variables: RawVariable[],
  rng: seedrandom.PRNG,
): NumericalComputationResult | null {
  const expression = normalizeString(
    question.variable_based_answer ?? question.variableBasedAnswer,
  );

  if (!expression) {
    return null;
  }

  for (let attempt = 0; attempt < 200; attempt += 1) {
    const generatedVariables = assignNumericalVariableValues(variables, rng);
    const substitutedExpression = substituteVariablesInExpression(
      expression,
      generatedVariables,
    );

    try {
      const evaluatedValue = Number(evaluate(substitutedExpression));

      if (!Number.isFinite(evaluatedValue) || Number.isNaN(evaluatedValue)) {
        continue;
      }

      const roundedAnswer = roundToSignificantFigures(evaluatedValue);

      if (!Number.isFinite(roundedAnswer) || Number.isNaN(roundedAnswer)) {
        continue;
      }

      return {
        correctAnswer: normalizeCorrectNumericalAnswer(roundedAnswer),
        evaluatedAnswer: roundedAnswer,
        variables: generatedVariables,
      };
    } catch {
      continue;
    }
  }

  return null;
}

function createFallbackWrongAnswer(
  correctAnswer: number,
  rng: seedrandom.PRNG,
): number {
  if (Math.abs(correctAnswer) < 1e-9) {
    return roundToSignificantFigures(rng.quick() + 0.1);
  }

  return roundToSignificantFigures(
    (0.5 * (rng.quick() + 0.1)) * correctAnswer,
  );
}

function resolveRandomSetQuestion(
  question: RawQuestion,
  variables: RawVariable[],
  seed: string,
): ResolvedRandomVariables {
  const clonedVariables = cloneVariables(variables);
  const contentPairs = getContentPairs(clonedVariables[0] ?? {});

  if (contentPairs.length === 0) {
    return {
      answerIndex: 0,
      correctAnswer: {},
      generatedOptions: null,
      variables: clonedVariables,
    };
  }

  const rng = seedrandom(seed);
  const selectedIndex = Math.min(
    contentPairs.length - 1,
    Math.floor(rng.quick() * contentPairs.length),
  );

  if (clonedVariables[0]) {
    clonedVariables[0].current_content_pair_index = selectedIndex;
    clonedVariables[0].currentContentPairIndex = selectedIndex;
  }

  const selectedPair = contentPairs[selectedIndex];
  const questionType = getQuestionType(question.type);
  const generatedOptions =
    questionType === 'multipleChoice'
      ? [
          getPairAnswerContent(selectedPair),
          ...contentPairs
            .filter((_, index) => index !== selectedIndex)
            .map((pair) => getPairAnswerContent(pair)),
        ].filter((contentSection): contentSection is RawContentSection =>
          Boolean(contentSection),
        )
      : null;

  return {
    answerIndex: 0,
    correctAnswer: resolvePairCorrectAnswer(selectedPair),
    generatedOptions,
    variables: clonedVariables,
  };
}

function resolveRandomNumericalQuestion(
  question: RawQuestion,
  variables: RawVariable[],
  seed: string,
): ResolvedRandomVariables {
  const questionType = getQuestionType(question.type);
  const initialRng = seedrandom(seed);
  const correctComputation = computeRandomNumericalAnswer(
    question,
    variables,
    initialRng,
  );

  if (!correctComputation) {
    // The answer expression is missing OR could not be evaluated (e.g. mathjs
    // reads implicit multiplication like "R3 (R1 + R2)" as a function call and
    // throws). Variable VALUE assignment must NOT depend on computing the answer:
    // we still assign concrete in-range values so the prompt's ##VAR##
    // placeholders are substituted before they reach the KaTeX parser. Only the
    // computed answer is left unresolved (the backend remains the grading source
    // of truth).
    return {
      answerIndex: 0,
      correctAnswer: {},
      generatedOptions: null,
      variables: assignNumericalVariableValues(variables, seedrandom(seed)),
    };
  }

  if (questionType !== 'multipleChoice') {
    return {
      answerIndex: 0,
      correctAnswer: correctComputation.correctAnswer,
      generatedOptions: null,
      variables: correctComputation.variables,
    };
  }

  const optionCount = getMultipleChoiceOptionCount(question);
  const optionValues = [correctComputation.evaluatedAnswer];
  const generatedOptions: RawContentSection[] = [
    createTextContentSection(
      formatResolvedAnswer(correctComputation.correctAnswer),
    ),
  ];

  for (let index = 1; index < optionCount; index += 1) {
    let candidate: NumericalComputationResult | null = null;

    for (let attempt = 0; attempt < 10; attempt += 1) {
      candidate = computeRandomNumericalAnswer(question, variables, initialRng);

      if (
        candidate &&
        differsByAtLeastTenPercent(candidate.evaluatedAnswer, optionValues)
      ) {
        break;
      }

      candidate = null;
    }

    if (!candidate) {
      const fallbackValue = createFallbackWrongAnswer(
        correctComputation.evaluatedAnswer,
        initialRng,
      );
      optionValues.push(fallbackValue);
      generatedOptions.push(
        createTextContentSection(
          formatResolvedAnswer(normalizeCorrectNumericalAnswer(fallbackValue)),
        ),
      );
      continue;
    }

    optionValues.push(candidate.evaluatedAnswer);
    generatedOptions.push(
      createTextContentSection(formatResolvedAnswer(candidate.correctAnswer)),
    );
  }

  const displayComputation =
    computeRandomNumericalAnswer(question, variables, seedrandom(seed)) ??
    correctComputation;

  return {
    answerIndex: 0,
    correctAnswer: displayComputation.correctAnswer,
    generatedOptions,
    variables: displayComputation.variables,
  };
}

// A fixed seed used to compute the instructor-facing "representative" set of
// variable values. Using a constant (instead of a per-user id) means every
// instructor previews the same concrete numbers for a given question, while each
// student still gets their own user-seeded values.
export const REPRESENTATIVE_VARIABLE_SEED = 'kudu-representative-preview';

/**
 * Resolve a representative set of variable values for instructor preview, reusing
 * the exact same logic students get — only the seed differs (a fixed constant
 * instead of the student's user id). This lets instructors see concrete numbers
 * (so `##VAR##` placeholders never reach the KaTeX parser) while making clear,
 * via a UI note, that students each receive their own randomized values.
 */
export function resolveRepresentativeRandomVariables(
  question: RawQuestion,
  isMultiPart: boolean,
): ResolvedRandomVariables {
  return resolveRandomVariables(question, REPRESENTATIVE_VARIABLE_SEED, isMultiPart);
}

/**
 * Resolve only the representative VALUES for each variable (no answer
 * computation), so `##VAR##` placeholders can be substituted for any
 * instructor-facing preview — even when the question has no
 * `variable_based_answer` to evaluate. Deterministic via the fixed
 * representative seed. Random-set questions pick a representative content pair;
 * numerical questions get an in-range value per variable.
 */
export function resolveRepresentativeVariableValues(
  question: RawQuestion,
  isMultiPart = false,
): RawVariable[] {
  const variables = toVariableArray(question.variables);

  if (variables.length === 0) {
    return [];
  }

  const seed = getSeedForUser(question, REPRESENTATIVE_VARIABLE_SEED, isMultiPart);

  if (isRandomSetQuestion(variables)) {
    return resolveRandomSetQuestion(question, variables, seed).variables;
  }

  return assignNumericalVariableValues(variables, seedrandom(seed));
}

export function resolveRandomVariables(
  question: RawQuestion,
  kuduUserId: string,
  isMultiPart: boolean,
): ResolvedRandomVariables {
  const variables = toVariableArray(question.variables);
  const seed = getSeedForUser(question, kuduUserId, isMultiPart);

  if (variables.length === 0) {
    return {
      answerIndex: 0,
      correctAnswer: {},
      generatedOptions: null,
      variables: [],
    };
  }

  return isRandomSetQuestion(variables)
    ? resolveRandomSetQuestion(question, variables, seed)
    : resolveRandomNumericalQuestion(question, variables, seed);
}
